// src/components/SignalsView.js
import React, { useState } from "react";
import { getIndicators } from "../api/signals";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ResponsiveContainer from "./ResponsiveContainer";

function fmt(v, digits = 2) {
  if (v === null || v === undefined || isNaN(Number(v))) return "--";
  return Number(v).toFixed(digits);
}

function IndicatorCard({ label, value, sub, color }) {
  return (
    <Paper
      variant="outlined"
      sx={{ p: 1.5, flex: 1, minWidth: 110, borderLeft: color ? `4px solid ${color}` : undefined }}
    >
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="h6" fontWeight={700} sx={{ mt: 0.5, fontSize: { xs: 16, sm: 20 } }}>
        {value}
      </Typography>
      {sub && (
        <Typography variant="caption" color="text.secondary">
          {sub}
        </Typography>
      )}
    </Paper>
  );
}

function rsiColor(rsi) {
  if (rsi === null || rsi === undefined) return undefined;
  if (rsi >= 70) return "#d32f2f";   // overbought
  if (rsi <= 30) return "#388e3c";   // oversold
  return "#1976d2";                  // neutral
}

const PERIODS = ["1mo", "3mo", "6mo", "1y", "2y"];
const INTERVALS = ["1d", "1wk"];

const SignalsView = () => {
  const [symbol, setSymbol] = useState("AAPL");
  const [period, setPeriod] = useState("3mo");
  const [interval, setInterval] = useState("1d");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleFetch = async (e) => {
    e.preventDefault();
    const sym = symbol.trim().toUpperCase();
    if (!sym) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const data = await getIndicators(sym, period, interval);
      setResult(data);
    } catch (err) {
      setError(err.message || "Failed to fetch indicators.");
    } finally {
      setLoading(false);
    }
  };

  const latest = result?.latest ?? {};
  const history = result?.history ?? [];

  const rsi = latest.rsi_14 !== undefined ? Number(latest.rsi_14) : null;
  const ema20 = latest.ema_20 !== undefined ? Number(latest.ema_20) : null;
  const macd = latest.macd !== undefined ? Number(latest.macd) : null;
  const stochK = latest.stoch_k !== undefined ? Number(latest.stoch_k) : null;
  const atr = latest.atr_14 !== undefined ? Number(latest.atr_14) : null;
  const close = latest.close !== undefined ? Number(latest.close) : null;

  const rsiLabel =
    rsi !== null
      ? rsi >= 70
        ? "Overbought"
        : rsi <= 30
        ? "Oversold"
        : "Neutral"
      : null;

  const macdSignal =
    macd !== null ? (macd > 0 ? "Bullish" : "Bearish") : null;

  return (
    <ResponsiveContainer>
      <Card elevation={3} sx={{ width: "100%" }}>
        <CardContent>
          <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: 22, sm: 28 } }}>
            Signals &amp; Indicators
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={2}>
            Live technical indicators for any symbol via yfinance.
          </Typography>

          <Box component="form" onSubmit={handleFetch}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} mb={2} alignItems="flex-start">
              <TextField
                label="Symbol"
                value={symbol}
                onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                size="small"
                sx={{ width: { xs: "100%", sm: 120 } }}
                inputProps={{ style: { textTransform: "uppercase" } }}
              />
              <FormControl size="small" sx={{ minWidth: 90 }}>
                <InputLabel>Period</InputLabel>
                <Select
                  label="Period"
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                >
                  {PERIODS.map((p) => (
                    <MenuItem key={p} value={p}>{p}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 90 }}>
                <InputLabel>Interval</InputLabel>
                <Select
                  label="Interval"
                  value={interval}
                  onChange={(e) => setInterval(e.target.value)}
                >
                  {INTERVALS.map((iv) => (
                    <MenuItem key={iv} value={iv}>{iv}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button type="submit" variant="contained" disabled={loading} sx={{ minHeight: 40 }}>
                {loading ? <CircularProgress size={20} /> : "Fetch"}
              </Button>
            </Stack>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {result && (
            <>
              <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight={700}>
                  {result.symbol}
                </Typography>
                <Chip label={result.period} size="small" variant="outlined" />
                <Chip label={result.interval} size="small" variant="outlined" />
                <Typography variant="caption" color="text.secondary">
                  {result.data_points} bars
                </Typography>
              </Stack>

              {/* KPI Row */}
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} mb={2} sx={{ flexWrap: "wrap" }}>
                <IndicatorCard
                  label="Price (close)"
                  value={close !== null ? `$${fmt(close)}` : "--"}
                />
                <IndicatorCard
                  label="RSI-14"
                  value={rsi !== null ? fmt(rsi, 1) : "--"}
                  sub={rsiLabel}
                  color={rsi !== null ? rsiColor(rsi) : undefined}
                />
                <IndicatorCard
                  label="EMA-20"
                  value={ema20 !== null ? `$${fmt(ema20)}` : "--"}
                  sub={close !== null && ema20 !== null ? (close > ema20 ? "Above EMA ↑" : "Below EMA ↓") : undefined}
                  color={close !== null && ema20 !== null ? (close > ema20 ? "#388e3c" : "#d32f2f") : undefined}
                />
                <IndicatorCard
                  label="MACD"
                  value={macd !== null ? fmt(macd, 3) : "--"}
                  sub={macdSignal}
                  color={macd !== null ? (macd > 0 ? "#388e3c" : "#d32f2f") : undefined}
                />
                <IndicatorCard
                  label="Stoch %K"
                  value={stochK !== null ? fmt(stochK, 1) : "--"}
                  sub={stochK !== null ? (stochK >= 80 ? "Overbought" : stochK <= 20 ? "Oversold" : "Neutral") : undefined}
                />
                <IndicatorCard
                  label="ATR-14"
                  value={atr !== null ? fmt(atr, 3) : "--"}
                  sub="Volatility measure"
                />
              </Stack>

              <Divider sx={{ my: 2 }} />

              {/* History Table */}
              {history.length > 0 && (
                <>
                  <Typography variant="subtitle1" fontWeight={600} mb={1}>
                    Indicator History (last {history.length} bars)
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ overflowX: "auto" }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell align="right">Close</TableCell>
                          <TableCell align="right">RSI-14</TableCell>
                          <TableCell align="right">EMA-20</TableCell>
                          <TableCell align="right">MACD</TableCell>
                          <TableCell align="right">Stoch %K</TableCell>
                          <TableCell align="right">ATR-14</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {[...history].reverse().map((row, idx) => {
                          const rowDate = row.date
                            ? String(row.date).split("T")[0]
                            : "--";
                          const rowRsi = row.rsi_14 !== undefined ? Number(row.rsi_14) : null;
                          return (
                            <TableRow key={idx} hover>
                              <TableCell sx={{ whiteSpace: "nowrap" }}>{rowDate}</TableCell>
                              <TableCell align="right">{row.close !== undefined ? `$${fmt(row.close)}` : "--"}</TableCell>
                              <TableCell align="right">
                                {rowRsi !== null ? (
                                  <Typography
                                    variant="body2"
                                    sx={{ color: rsiColor(rowRsi), fontWeight: rowRsi >= 70 || rowRsi <= 30 ? 700 : 400 }}
                                  >
                                    {fmt(rowRsi, 1)}
                                  </Typography>
                                ) : "--"}
                              </TableCell>
                              <TableCell align="right">{row.ema_20 !== undefined ? `$${fmt(row.ema_20)}` : "--"}</TableCell>
                              <TableCell align="right">{row.macd !== undefined ? fmt(row.macd, 3) : "--"}</TableCell>
                              <TableCell align="right">{row.stoch_k !== undefined ? fmt(row.stoch_k, 1) : "--"}</TableCell>
                              <TableCell align="right">{row.atr_14 !== undefined ? fmt(row.atr_14, 3) : "--"}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </ResponsiveContainer>
  );
};

export default SignalsView;
